import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { authService, memberService } from "../../services/api";

// Cache duration in milliseconds (5 minutes)
const TOKEN_CACHE_DURATION = 5 * 60 * 1000;

const RouteGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);

  // Refs to store cache and debounce timer
  const cacheRef = useRef({
    lastValidation: null,
    isValid: false,
    passwordChangeRequired: false,
  });
  const debounceTimerRef = useRef(null);

  // Function to clear cache
  const clearCache = useCallback(() => {
    cacheRef.current = {
      lastValidation: null,
      isValid: false,
      passwordChangeRequired: false,
    };
  }, []);

  // Function to validate token with caching
  const validateToken = useCallback(
    async (forceRefresh = false) => {
      const now = Date.now();
      const cache = cacheRef.current;

      // Check if we can use cached result
      if (
        !forceRefresh &&
        cache.lastValidation &&
        now - cache.lastValidation < TOKEN_CACHE_DURATION
      ) {
        setIsValidating(false);
        setIsValid(cache.isValid);
        return cache;
      }

      // Skip validation for public routes
      const publicRoutes = [
        "/",
        "/login",
        "/forgot-password",
        "/register",
        "/facebook-callback",
        "/admin/facebook-callback",
      ];

      if (publicRoutes.includes(location.pathname)) {
        setIsValidating(false);
        setIsValid(true);
        cacheRef.current = {
          lastValidation: now,
          isValid: true,
          passwordChangeRequired: false,
        };
        return cacheRef.current;
      }

      // Check if token exists
      if (!authService.isAuthenticated()) {
        setIsValidating(false);
        setIsValid(false);
        clearCache();
        navigate("/login", {
          state: { from: location.pathname },
          replace: true,
        });
        return { isValid: false, passwordChangeRequired: false };
      }

      try {
        // Call /api/v1/member/me to validate token
        const response = await memberService.getProfile();

        let passwordChangeRequired = false;

        // Check if password change is required (HTTP 423)
        if (
          response.status === 423 ||
          (response.data &&
            response.data.message === "Password change required")
        ) {
          passwordChangeRequired = true;

          // Only redirect to change password if not already there
          if (location.pathname !== "/change-password") {
            navigate("/change-password", {
              state: { passwordChangeRequired: true },
              replace: true,
            });
          }
        }

        // Update cache
        cacheRef.current = {
          lastValidation: now,
          isValid: true,
          passwordChangeRequired,
        };

        setIsValidating(false);
        setIsValid(true);

        return cacheRef.current;
      } catch (error) {
        // Token is invalid or expired
        console.warn("Token validation failed:", error);

        // Clear invalid tokens and cache
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user");
        clearCache();

        setIsValidating(false);
        setIsValid(false);

        navigate("/login", {
          state: { from: location.pathname },
          replace: true,
        });

        return { isValid: false, passwordChangeRequired: false };
      }
    },
    [navigate, location.pathname, clearCache],
  );

  // Debounced validation function
  const debouncedValidation = useCallback(
    (forceRefresh = false) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        validateToken(forceRefresh);
      }, 300); // 300ms debounce
    },
    [validateToken],
  );

  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Perform validation
    debouncedValidation();

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [location.pathname, debouncedValidation]);

  // Set global functions
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.routeGuardRefresh = () => {
        clearCache();
        return validateToken(true);
      };
      window.routeGuardClearCache = clearCache;
    }

    return () => {
      if (typeof window !== "undefined") {
        window.routeGuardRefresh = null;
        window.routeGuardClearCache = null;
      }
    };
  }, [clearCache, validateToken]);

  // Listen for storage events (for multi-tab sync)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // If token was removed from another tab, force logout
      if (e.key === "token" && !e.newValue) {
        clearCache();
        navigate("/login", {
          state: { from: location.pathname },
          replace: true,
        });
        return;
      }

      // If token was updated, force refresh validation
      if (e.key === "token" && e.newValue) {
        validateToken(true);
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [navigate, location.pathname, validateToken, clearCache]);

  // Show loading spinner while validating
  if (isValidating) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-light">
        <div className="text-center p-4">
          <div className="mb-3">
            <Spinner
              animation="border"
              variant="primary"
              role="status"
              style={{ width: "3rem", height: "3rem" }}
            >
              <span className="visually-hidden">Validating...</span>
            </Spinner>
          </div>
          <h5 className="text-primary mb-2">Validating Your Session</h5>
          <p className="text-muted mb-0">
            Please wait while we verify your authentication...
          </p>
        </div>
      </div>
    );
  }

  // If not valid, don't render children (navigation will happen automatically)
  if (!isValid) {
    return null;
  }

  // If valid, render children
  return children;
};

// Export a hook to access refresh validation function if needed
export const useRouteGuardRefresh = () => {
  // This hook can be used in components to force refresh token validation
  // Example: const refreshValidation = useRouteGuardRefresh();
  // Usage: refreshValidation();

  return useCallback(() => {
    // Use the global refresh function if available
    if (typeof window !== "undefined" && window.routeGuardRefresh) {
      return window.routeGuardRefresh();
    }

    // Fallback to clearing cache only
    if (typeof window !== "undefined" && window.routeGuardClearCache) {
      window.routeGuardClearCache();
    }
  }, []);
};

// Make refresh function globally accessible
if (typeof window !== "undefined") {
  window.routeGuardRefresh = null;
  window.routeGuardClearCache = null;
}

export default RouteGuard;
